import { Container, Graphics, TilingSprite, Ticker } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { AudioBus, type SfxLoop } from '../../audio/AudioBus';
import { finalLanding, REEL_SOUNDS } from '../../audio/reelSounds';
import { DefaultRandom, type Random } from '../../core/Random';
import { PaylineHighlighter } from './PaylineHighlighter';
import { SlotMachineFrame } from './SlotMachineFrame';
import { SlotReel } from './SlotReel';
import type { LineSettlement } from './settleSpin';
import type { SlotMachineConfig, SlotSymbolDef, SpinOutcome } from './types';
import type { WinLine } from './WinLine';

const REEL_SPACING = 8;
/**
 * Default symmetric padding around the reel grid — same on every side.
 * Earlier the top padding was bumped to 36 to clear the HOLD pill (a
 * hold-and-spin affordance that floats above each reel); the round-
 * trip on that was that the asymmetric frame read awkward when the
 * rest of the slot scene had a painterly background. UX feedback:
 * "padding should be the same all way round."
 *
 * <p>20 is enough that the HOLD pill (when it appears) still has room
 * inside the frame curve without clipping the rounded-rectangle stroke.
 * If the pill ever needs more clearance, the right fix is to shrink the
 * pill or move it INSIDE the frame, not to break frame symmetry again.
 *
 * <p>Themes that ship a {@code scene.border} overlay (asset brief pack 2
 * §20) override this via {@code config.chrome.framePad} so the
 * painted-ring overlay sits cleanly in the gutter without clipping
 * the outer reel cells.
 */
const DEFAULT_FRAME_PAD = 20;

/**
 * N reels in a row, plus framing chrome. Coordinates the spin/stagger-stop
 * sequence: each reel starts free-spinning together, then stops one at a time
 * with a configurable inter-reel delay (the "left-to-right reveal" rhythm
 * that makes slot reveals feel deliberate rather than chaotic).
 *
 * <p>v0 generates each reel's final symbols with weighted-random independent
 * draws — no payline / win-line awareness yet. cs-core's WindowConfiguration +
 * WinLines + LineSettlement model lands in the next chunk.
 */
export class SlotMachine extends Container {
  private readonly reels: SlotReel[] = [];
  private readonly random: Random;
  private readonly highlighter: PaylineHighlighter;
  /** Effective symmetric frame padding (config override or default). */
  private readonly framePad: number;

  constructor(
    private readonly ticker: Ticker,
    public readonly config: SlotMachineConfig,
    randomImpl?: Random,
  ) {
    super();
    this.random = randomImpl ?? new DefaultRandom();

    const { reelCount, visibleRows, symbolSize, symbols, spin, chrome } = config;
    const accentColor = chrome?.accentColor;
    const frameFillColor = chrome?.frameFillColor;
    const framePad = chrome?.framePad ?? DEFAULT_FRAME_PAD;
    this.framePad = framePad;
    const totalWidth = reelCount * symbolSize + (reelCount - 1) * REEL_SPACING;
    const totalHeight = visibleRows * symbolSize;

    if (!chrome?.omitInnerFrame) {
      this.addChild(
        new SlotMachineFrame({
          innerWidth: totalWidth,
          innerHeight: totalHeight,
          padSide: framePad,
          padTop: framePad,
          padBottom: framePad,
          accentColor,
          fillColor: frameFillColor,
        }),
      );
    }

    // Per-reel-column tiled backplate — sits BEHIND each reel column,
    // between the frame fill and the symbols. Vertically-tiling
    // texture sourced from {@code theme.assets.scene.columnBackground}
    // (asset brief pack 2 §19). Skipped silently when the theme
    // doesn't ship one — the frame fill stands in.
    const columnBgAlias = chrome?.columnBackgroundAlias;
    const columnBgTexture = columnBgAlias
      ? AssetLoader.shared.getTexture(columnBgAlias)
      : null;
    if (columnBgTexture) {
      for (let i = 0; i < reelCount; i++) {
        const colX = i * (symbolSize + REEL_SPACING);
        const tile = new TilingSprite({
          texture: columnBgTexture,
          width: symbolSize,
          height: totalHeight,
        });
        // Cover-fit the texture to the column width (the source is
        // 768px wide; column is ~72-122px depending on machine scale).
        const scale = symbolSize / columnBgTexture.width;
        tile.tileScale.set(scale);
        tile.x = colX;
        tile.y = 0;
        this.addChild(tile);
      }
    }

    // Black vertical dividers between adjacent reel columns — gives the
    // machine a "physical slot machine" feel where each reel is its
    // own slot cut into the backplate. Drawn AFTER the frame +
    // column-bg but BEFORE the reels so the divider lines sit between
    // the playing surface and the symbols. (Reference: the Farm-themed
    // slot screenshot from Jon's playtest feedback.)
    const dividers = new Graphics();
    for (let i = 1; i < reelCount; i++) {
      // Midpoint between reel (i-1) and reel i, in machine-local coords
      // (origin at top-left of reel grid).
      const x = i * (symbolSize + REEL_SPACING) - REEL_SPACING / 2;
      dividers.rect(x - 1.5, 0, 3, totalHeight);
    }
    dividers.fill({ color: 0x000000, alpha: 0.85 });
    this.addChild(dividers);

    for (let i = 0; i < reelCount; i++) {
      const reel = new SlotReel(
        this.ticker,
        this.random,
        symbols,
        visibleRows,
        symbolSize,
        spin.scrollSpeed,
        accentColor,
      );
      reel.x = i * (symbolSize + REEL_SPACING) + symbolSize / 2;
      reel.y = 0;
      this.addChild(reel);
      this.reels.push(reel);
    }

    // Highlighter sits on top of reels but inside the machine frame, so its
    // payline strokes draw over the reels at win time. Cleared at spin start.
    this.highlighter = new PaylineHighlighter(symbolSize, REEL_SPACING, accentColor);
    this.addChild(this.highlighter);
  }

  /** Width of the machine including frame padding. */
  totalWidth(): number {
    const { reelCount, symbolSize } = this.config;
    return reelCount * symbolSize + (reelCount - 1) * REEL_SPACING + this.framePad * 2;
  }

  /** Height of the machine including frame padding. */
  totalHeight(): number {
    return this.config.visibleRows * this.config.symbolSize + this.framePad * 2;
  }

  /** Distance from the top of the bounding box {@link totalHeight} provides to
   * the top of the reel grid — used by host scenes when they want to anchor
   * something to the reel grid's vertical centre. */
  topPadding(): number {
    return this.framePad;
  }

  /**
   * Spin all reels, then stop them left-to-right with the configured stagger.
   * Resolves with the visible grid once every reel has come to rest.
   *
   * <p>Held reels (those with {@link SlotReel.isHeld} true) skip the spin and
   * keep their currently-visible symbols. The returned grid still includes
   * those rows for consistency — settlement / display sees a complete grid
   * regardless of which reels actually moved. Holds clear after the spin so
   * the player has to re-tap to hold for the next round.
   *
   * <p>{@code winMultiple} scores the drawn grid (its win as a multiple of
   * the bet) so the last reel can land with coins on a win.
   */
  async spin(options: { winMultiple?: (outcome: SpinOutcome) => number } = {}): Promise<SpinOutcome> {
    const outcome = this.generateOutcomeRespectingHolds();
    const multiple = options.winMultiple?.(outcome);
    return this.runSpin(multiple === undefined ? outcome : { ...outcome, winMultiple: multiple }, {
      respectReelHolds: true,
    });
  }

  /**
   * Hold-and-spin bonus respin. Every reel scrolls (no per-reel holds —
   * locked cells are tracked at cell granularity, not reel), but each
   * reel's outcome is constrained: cells listed in {@code lockedDefs}
   * land back on their existing def, while unlocked cells draw fresh
   * weighted picks from {@code bonusSymbols} (or {@link config.symbols}
   * when not provided).
   *
   * <p>Why this works without a per-cell freeze animation: the player
   * sees the value chips (rendered by an overlay layer in the host scene)
   * stay pinned to their grid positions. Beneath, the reel does the full
   * scroll-and-land — and lands with the locked symbol back where it was.
   * The visual reads as "the gold pieces stayed where they were while
   * everything else respun." The same trick is used in shipped MMS-style
   * slots (Lightning Link, Cash Express, Dragon Link).
   */
  async respinUnlocked(args: {
    /** Map keyed {@code "col:row"} → the def each locked cell should land
     * on. Cells not in the map respin freely. */
    lockedDefs: ReadonlyMap<string, SlotSymbolDef>;
    /** Optional alternative symbol roster — typically the regular set with
     * money-symbol weight bumped so respins land more money symbols
     * than base spins. Falls back to {@link config.symbols}. */
    bonusSymbols?: readonly SlotSymbolDef[];
  }): Promise<SpinOutcome> {
    return this.runSpin(this.generateRespinOutcome(args), {
      respectReelHolds: false,
    });
  }

  /** Common spin runner — kicks off the reels, awaits the stagger-stop,
   * resolves with the grid. The two callers differ only in (a) which
   * outcome they pre-computed and (b) whether held reels skip the spin. */
  private async runSpin(
    outcome: SpinOutcome,
    options: { respectReelHolds: boolean },
  ): Promise<SpinOutcome> {
    this.startSpinningInternal(options);
    await this.stopOnOutcomeInternal(outcome, options);
    return outcome;
  }

  /**
   * Phase D — server-driven spin. Caller supplies a promise that
   * resolves to the (server-authoritative) outcome; the machine starts
   * scrolling immediately and only stops when (a) the minimum spin
   * duration has elapsed AND (b) the server result has arrived. This
   * decouples local animation pacing from server latency: a fast server
   * doesn't cut the spin short, a slow one extends the scroll.
   *
   * <p>{@code respectReelHolds} defaults to false — server-mode treats
   * the grid as fully server-determined, so per-reel client holds would
   * desync. Local-mode {@link spin} keeps holds enabled.
   */
  async spinDriven(
    outcomePromise: Promise<SpinOutcome>,
    options: { respectReelHolds?: boolean } = {},
  ): Promise<SpinOutcome> {
    const { respectReelHolds = false } = options;
    return this.spinDrivenInternal(outcomePromise, { respectReelHolds });
  }

  private async spinDrivenInternal(
    outcomePromise: Promise<SpinOutcome>,
    options: { respectReelHolds: boolean },
  ): Promise<SpinOutcome> {
    this.startSpinningInternal(options);
    let outcome: SpinOutcome;
    try {
      outcome = await outcomePromise;
    } catch (err) {
      // No result to land on (the server failed): the whirr stops with it.
      this.stopSpinLoop();
      throw err;
    }
    await this.stopOnOutcomeInternal(outcome, options);
    return outcome;
  }

  /** Start every reel scrolling. No outcome computation, no min-spin
   * wait, no stop scheduling — caller drives the rest via
   * {@link stopOnOutcomeInternal}. Held reels (when respectReelHolds is
   * true) skip; otherwise their hold flag is cleared. */
  private startSpinningInternal(options: { respectReelHolds: boolean }): void {
    this.highlighter.clear();
    let moving = 0;
    for (const reel of this.reels) {
      if (!options.respectReelHolds && reel.isHeld()) {
        reel.setHeld(false);
      }
      if (!reel.isHeld()) moving += 1;
      reel.spin();
    }
    if (moving === 0) return;
    // The reels set off with a whoosh, and whirr until they land.
    AudioBus.shared.play(REEL_SOUNDS.start);
    this.stopSpinLoop(60);
    this.spinLoop = AudioBus.shared.playLoop(REEL_SOUNDS.loop, {
      volume: REEL_SOUNDS.loopVolume,
      loopStart: REEL_SOUNDS.loopStart,
      loopLength: REEL_SOUNDS.loopLength,
      fadeInMs: 150,
    });
  }

  /** The whirr while the reels spin (see {@link startSpinningInternal}). */
  private spinLoop: SfxLoop | null = null;

  private stopSpinLoop(fadeOutMs = 160): void {
    this.spinLoop?.stop(fadeOutMs);
    this.spinLoop = null;
  }

  /** Wait the configured min-spin duration, then stagger-stop the reels
   * onto {@code outcome}. Resolves once every animating reel has come
   * to rest. Releases per-reel holds in respectReelHolds mode. */
  private async stopOnOutcomeInternal(
    outcome: SpinOutcome,
    options: { respectReelHolds: boolean },
  ): Promise<void> {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, this.config.spin.minSpinMs);
    });

    await new Promise<void>((resolveAll) => {
      let pending = 0;
      this.reels.forEach((r) => {
        if (options.respectReelHolds && r.isHeld()) return;
        pending += 1;
      });
      if (pending === 0) {
        this.stopSpinLoop();
        resolveAll();
        return;
      }
      const moving = pending;
      this.reels.forEach((reel, index) => {
        if (options.respectReelHolds && reel.isHeld()) return;
        const finalSymbols = outcome.grid[index]!;
        setTimeout(() => {
          reel.stop(finalSymbols, this.config.spin.stopDurationMs, () => {
            // Each reel lands with a thunk, the last one with coins when
            // the spin won, and the whirr thins out as they stop.
            AudioBus.shared.play(pending === 1 ? finalLanding(outcome.winMultiple, index) : REEL_SOUNDS.land(index));
            pending--;
            this.spinLoop?.setVolume((REEL_SOUNDS.loopVolume * pending) / moving, 120);
            if (pending === 0) {
              this.stopSpinLoop();
              resolveAll();
            }
          });
        }, index * this.config.spin.staggerMs);
      });
    });

    if (options.respectReelHolds) {
      for (const reel of this.reels) {
        reel.setHeld(false);
      }
    }
  }

  /** Per-cell pixel position of the centre of a grid cell, in the
   * machine's local coordinate space. Used by overlays (money-value
   * chips, cell highlights) that need to follow locked cells across
   * resizes. */
  cellCenter(col: number, row: number): { x: number; y: number } {
    const { symbolSize } = this.config;
    return {
      x: col * (symbolSize + REEL_SPACING) + symbolSize / 2,
      y: row * symbolSize + symbolSize / 2,
    };
  }

  /** Width × height of a single grid cell — used by overlays to size
   * per-cell decorations. */
  cellSize(): number {
    return this.config.symbolSize;
  }

  /** Toggle whether each reel listens for tap-to-hold. Host typically enables
   * between spins and disables during one. */
  setHoldInteractive(value: boolean): void {
    for (const reel of this.reels) {
      reel.setHoldInteractive(value);
    }
  }

  /**
   * Visual celebration of a settled spin: pulse each winning cell on its reel
   * and draw glowing strokes along each contributing payline. Resolves when
   * the highlight animation has finished — callers can use it to pace the
   * post-spin take-win sequence.
   *
   * <p>No-op (immediately resolves) when both {@code settlements} and
   * {@code scatterPositions} are empty.
   */
  async highlightWins(
    settlements: readonly LineSettlement[],
    winLines: readonly WinLine[],
    scatterPositions: readonly { col: number; row: number }[] = [],
  ): Promise<void> {
    if (settlements.length === 0 && scatterPositions.length === 0) {
      return;
    }

    // Celebrate winning cells. A cell may be on multiple winning lines —
    // animate it once, not N times — so dedupe by (col, row) before
    // triggering. Scatter cells join the dedupe so a scatter that
    // happens to also sit on a payline still animates just once.
    //
    // {@link SlotReel#celebrateCellAtRow} prefers the symbol's
    // sprite-sheet win animation when the theme ships one (asset
    // brief pack 2 §22) and falls back to the scale-pulse for emoji /
    // partial-art themes. Calls fire-and-forget so the payline glow
    // (showWinningLines below) animates in parallel.
    const celebrated = new Set<string>();
    const celebrateCell = (col: number, row: number): void => {
      const key = `${col}-${row}`;
      if (celebrated.has(key)) return;
      celebrated.add(key);
      void this.reels[col]?.celebrateCellAtRow(row);
    };

    const linesById = new Map(winLines.map((l) => [l.id, l]));
    for (const settlement of settlements) {
      const line = linesById.get(settlement.lineId);
      if (!line) continue;
      for (let i = 0; i < settlement.matchCount; i++) {
        const pos = line.positions[i];
        if (!pos) continue;
        celebrateCell(pos.col, pos.row);
      }
    }
    for (const pos of scatterPositions) {
      celebrateCell(pos.col, pos.row);
    }

    if (settlements.length > 0) {
      await this.highlighter.showWinningLines(settlements, winLines);
    }
  }

  /**
   * Light-touch "token collected" pulse on cells the server flagged as
   * bonus drops. Used by the multiplayer Reel Race so the player sees
   * which cell awarded a token without the heavy per-symbol
   * win-celebration video — that treatment is reserved for actual
   * payline wins (3+ matching symbols), and reusing it for every
   * single-bonus landing was conflating "you collected a token" with
   * "you won a payline".
   *
   * <p>Uses the same {@link SlotReel#flashCellAtRow} scale-pulse the
   * non-themed-slot fallback uses inside {@link highlightWins} —
   * brief (~1.1s) so the meter-side {@code +N} burst (rendered by
   * WheelReelRaceView near the player avatar) lands as the canonical
   * "added to your tokens" signal. Dedupes positions; fire-and-forget
   * — each tween cleans itself up on completion.
   */
  playBonusCollect(positions: readonly { col: number; row: number }[]): void {
    if (positions.length === 0) return;
    const seen = new Set<string>();
    for (const pos of positions) {
      const key = `${pos.col}-${pos.row}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const reel = this.reels[pos.col];
      if (!reel) continue;
      reel.flashCellAtRow(pos.row);
    }
  }

  /**
   * Item-drop cycle-and-settle reveal. After the reel lands an
   * {@code item_drop} symbol at {@code position}, this method runs a
   * three-phase animation in place:
   *
   * <ol>
   *   <li><b>Pulse</b> — quick scale-pulse on the star (~ same beat as
   *       {@link #playBonusCollect}), confirming the cell as the focal.
   *   <li><b>Cycle</b> — the cell's symbol rapidly rotates through
   *       {@code cycleDefs} (the full item-icon set) with an ease-out
   *       so the swap interval lengthens toward the end, building the
   *       "what did I get?" tension.
   *   <li><b>Settle</b> — swap to {@code finalDef} and scale-punch the
   *       reveal beat.
   * </ol>
   *
   * <p>Designed for the multiplayer Reel Race's loadout-drop reveal.
   * The host scene (mobile {@code WheelReelRaceView}) detects the drop
   * via {@code WheelSeatView.lastSpinItemCell}, builds the def list
   * from the registered {@code item:{ID}} asset aliases, and calls
   * this method. Returns when the settle finishes so the host can
   * chain a chip-fly arc to the BalancePill / inventory afterwards.
   */
  async playItemDropReveal(
    position: { col: number; row: number },
    cycleDefs: readonly SlotSymbolDef[],
    finalDef: SlotSymbolDef,
    options: { pulseDurationMs?: number; cycleDurationMs?: number; settleDurationMs?: number } = {},
  ): Promise<void> {
    const reel = this.reels[position.col];
    if (!reel) return;
    // Phase A — light pulse (mirrors playBonusCollect). Fire-and-forget;
    // the tween's totalDurationMs gates how long the pulse runs but
    // we overlap into Phase B so the reveal feels continuous rather
    // than serialised pulse → wait → cycle. Producer brief 2026-05-13
    // tightened to 100ms alongside cycle 750 + settle 250 for a 1000ms
    // perceived total reveal.
    reel.flashCellAtRow(position.row, options.pulseDurationMs ?? 100);
    // Phases B + C live inside SlotReel#cycleAndSettleAtRow so the
    // animation has direct access to the cell's SlotSymbol.
    await reel.cycleAndSettleAtRow(position.row, cycleDefs, finalDef, {
      cycleDurationMs: options.cycleDurationMs,
      settleDurationMs: options.settleDurationMs,
    });
  }

  /**
   * Force every reel to abandon any in-flight spin/stop and snap to a
   * clean idle state. Used by error-recovery paths in the host scene
   * (e.g. server-driven spin failed, outcome promise rejected) — the
   * reels would otherwise be left scrolling forever because the
   * normal stop sequence assumes a real outcome arrives. Visible
   * symbols are whatever was on the strip at cancel time.
   */
  forceLandAll(): void {
    this.stopSpinLoop(80);
    for (const reel of this.reels) {
      reel.forceLand();
    }
  }

  destroyMachine(): void {
    this.stopSpinLoop(40);
    for (const reel of this.reels) {
      reel.destroyReel();
    }
    this.highlighter.clear();
  }

  private generateOutcomeRespectingHolds(): SpinOutcome {
    const grid: SlotSymbolDef[][] = [];
    for (let r = 0; r < this.config.reelCount; r++) {
      const reel = this.reels[r]!;
      if (reel.isHeld()) {
        // Held reel: outcome row is whatever's currently visible.
        grid.push([...reel.visibleSymbolDefs()]);
      } else {
        const reelSymbols: SlotSymbolDef[] = [];
        for (let row = 0; row < this.config.visibleRows; row++) {
          reelSymbols.push(this.weightedPick(this.config.symbols));
        }
        grid.push(reelSymbols);
      }
    }
    return { grid };
  }

  /** Hold-and-spin outcome: locked cells land on their existing def,
   * unlocked cells draw fresh weighted picks from the bonus roster.
   * Per-reel hold flags are ignored (cell-level locks supersede). */
  private generateRespinOutcome(args: {
    lockedDefs: ReadonlyMap<string, SlotSymbolDef>;
    bonusSymbols?: readonly SlotSymbolDef[];
  }): SpinOutcome {
    const roster = args.bonusSymbols ?? this.config.symbols;
    const grid: SlotSymbolDef[][] = [];
    for (let col = 0; col < this.config.reelCount; col++) {
      const reelSymbols: SlotSymbolDef[] = [];
      for (let row = 0; row < this.config.visibleRows; row++) {
        const locked = args.lockedDefs.get(`${col}:${row}`);
        if (locked) {
          reelSymbols.push(locked);
        } else {
          reelSymbols.push(this.weightedPick(roster));
        }
      }
      grid.push(reelSymbols);
    }
    return { grid };
  }

  private weightedPick(roster: readonly SlotSymbolDef[]): SlotSymbolDef {
    const totalWeight = roster.reduce((sum, s) => sum + s.weight, 0);
    let pick = this.random.nextFloat() * totalWeight;
    for (const symbol of roster) {
      pick -= symbol.weight;
      if (pick <= 0) {
        return symbol;
      }
    }
    return roster[roster.length - 1]!;
  }
}
